import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Star, 
  Search, 
  Clock,
  Play,
  Pause,
  Volume2,
  VolumeX,
  SkipBack,
  SkipForward,
  X,
  Loader2,
  LogIn,
  Link2,
  ChevronLeft,
  ChevronRight,
  Moon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { YouTubeSearchKeyboard } from "@/components/youtube/YouTubeSearchKeyboard";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/useTranslation";
import { useAuth } from "@/hooks/useAuth";
import { lovable } from "@/integrations/lovable/index";
import { MediaRestMode } from "@/components/modals/MediaRestMode";

interface VideoItem {
  id: string;
  title: string;
  channel: string;
  views: string;
  duration: string;
  thumbnail: string;
  videoId?: string;
}

declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
  }
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.06 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1 },
};

// Demo videos with real YouTube video IDs (shown when no search)
const demoVideos: VideoItem[] = [
  {
    id: "1",
    title: "מוזיקה מרגיעה לרגיעה ומדיטציה",
    channel: "Relaxing Music",
    views: "10M צפיות",
    duration: "3:00:00",
    thumbnail: "https://img.youtube.com/vi/lFcSrYw-ARY/hqdefault.jpg",
    videoId: "lFcSrYw-ARY",
  },
  {
    id: "2",
    title: "נופים יפים מרחבי העולם",
    channel: "Nature Relaxation",
    views: "5M צפיות",
    duration: "1:00:00",
    thumbnail: "https://img.youtube.com/vi/BHACKCNDMW8/hqdefault.jpg",
    videoId: "BHACKCNDMW8",
  },
  {
    id: "3",
    title: "שירי ילדים בעברית",
    channel: "Hebrew Kids Songs",
    views: "2M צפיות",
    duration: "45:00",
    thumbnail: "https://img.youtube.com/vi/qJlGXdKPXho/hqdefault.jpg",
    videoId: "qJlGXdKPXho",
  },
  {
    id: "4",
    title: "יוגה למתחילים",
    channel: "Yoga With Adriene",
    views: "15M צפיות",
    duration: "20:00",
    thumbnail: "https://img.youtube.com/vi/v7AYKMP6rOE/hqdefault.jpg",
    videoId: "v7AYKMP6rOE",
  },
  {
    id: "5",
    title: "מוזיקה קלאסית מרגיעה",
    channel: "Classical Music",
    views: "8M צפיות",
    duration: "2:00:00",
    thumbnail: "https://img.youtube.com/vi/jgpJVI3tDbY/hqdefault.jpg",
    videoId: "jgpJVI3tDbY",
  },
  {
    id: "6",
    title: "צלילי טבע - גשם ורעמים",
    channel: "Nature Sounds",
    views: "20M צפיות",
    duration: "8:00:00",
    thumbnail: "https://img.youtube.com/vi/mPZkdNFkNps/hqdefault.jpg",
    videoId: "mPZkdNFkNps",
  },
];

type Tab = "home" | "search" | "history";

interface HistoryVideo {
  id: string;
  video_id: string;
  title: string;
  channel: string | null;
  thumbnail: string | null;
  duration: string | null;
  watched_at: string;
  watch_count: number;
}

export default function YouTube() {
  const navigate = useNavigate();
  const { t, isRTL, lang } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>("home");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<VideoItem[]>([]);
  const [searchPhase, setSearchPhase] = useState<"typing" | "results">("typing");
  const [homeVideos, setHomeVideos] = useState<VideoItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingHome, setIsLoadingHome] = useState(false);
  const [currentVideo, setCurrentVideo] = useState<VideoItem | null>(null);
  const [videoSource, setVideoSource] = useState<"home" | "search">("home");
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playerReady, setPlayerReady] = useState(false);
  const [playerInitialized, setPlayerInitialized] = useState(false);
  const [historyVideos, setHistoryVideos] = useState<HistoryVideo[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [googleAccessToken, setGoogleAccessToken] = useState<string | null>(null);
  const [isConnectingGoogle, setIsConnectingGoogle] = useState(false);
  const [showLinkAccountDialog, setShowLinkAccountDialog] = useState(false);
  const [videoPage, setVideoPage] = useState(0);
  const [isMediaRestMode, setIsMediaRestMode] = useState(false);
  const playerRef = useRef<any>(null);
  const timeUpdateInterval = useRef<NodeJS.Timeout | null>(null);

  // Get current videos to display based on tab and auth
  const getDisplayVideos = useCallback((): VideoItem[] => {
    if (activeTab === "history") {
      return historyVideos.map(h => ({
        id: h.id,
        title: h.title,
        channel: h.channel || "",
        views: `${h.watch_count} ${t("timesWatched")}`,
        duration: h.duration || "",
        thumbnail: h.thumbnail || "",
        videoId: h.video_id,
      }));
    }
    if (activeTab === "home" && homeVideos.length > 0) return homeVideos;
    if (searchResults.length > 0) return searchResults;
    return [];
  }, [activeTab, historyVideos, homeVideos, searchResults, t]);

  const displayVideos = getDisplayVideos();
  // Treat the user as "Google-connected" if any of their identities is Google
  // (covers both: signed in with Google, AND email user who linked Google).
  const isGoogleUser = !!(
    user?.app_metadata?.provider === 'google' ||
    user?.identities?.some(id => id.provider === 'google')
  );

  // Email user = signed in with an email identity (regardless of Google linking).
  const isEmailUser = !!user?.identities?.some(id => id.provider === 'email');

  // Has Google already been linked to this account?
  const hasGoogleLinked = !!user?.identities?.some(id => id.provider === 'google');

  // Pagination: 2 rows × 3 cols = 6 per page
  const VIDEOS_PER_PAGE = 6;
  const totalVideoPages = Math.ceil(displayVideos.length / VIDEOS_PER_PAGE);
  const pageVideos = displayVideos.slice(videoPage * VIDEOS_PER_PAGE, (videoPage + 1) * VIDEOS_PER_PAGE);
  const hasPrevVideoPage = videoPage > 0;
  const hasNextVideoPage = videoPage < totalVideoPages - 1;

  const tabs = [
    { id: "home" as Tab, label: t("popular"), icon: Star },
    { id: "search" as Tab, label: t("searchTab"), icon: Search },
    { id: "history" as Tab, label: t("history"), icon: Clock },
  ];

  // Load watch history when user is logged in
  const loadHistory = useCallback(async () => {
    if (!user) {
      setHistoryVideos([]);
      return;
    }
    
    setIsLoadingHistory(true);
    try {
      const { data, error } = await supabase
        .from('user_youtube_history')
        .select('*')
        .eq('user_id', user.id)
        .order('watched_at', { ascending: false })
        .limit(50);
      
      if (error) {
        console.error('[YouTube] Failed to load history:', error);
        return;
      }
      
      setHistoryVideos(data || []);
    } catch (err) {
      console.error('[YouTube] History load error:', err);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [user]);

  // Save video to history when watched
  const saveToHistory = useCallback(async (video: VideoItem) => {
    if (!user || !video.videoId) return;
    
    try {
      // Use edge function or direct call to the security definer function
      // First try upsert - this works if no existing record
      const { data: existing } = await supabase
        .from('user_youtube_history')
        .select('id, watch_count')
        .eq('user_id', user.id)
        .eq('video_id', video.videoId)
        .maybeSingle();
      
      if (existing) {
        // Update existing record
        await supabase
          .from('user_youtube_history')
          .update({
            watched_at: new Date().toISOString(),
            watch_count: existing.watch_count + 1,
            title: video.title,
            channel: video.channel,
            thumbnail: video.thumbnail,
            duration: video.duration,
          })
          .eq('id', existing.id);
      } else {
        // Insert new record
        await supabase
          .from('user_youtube_history')
          .insert({
            user_id: user.id,
            video_id: video.videoId,
            title: video.title,
            channel: video.channel,
            thumbnail: video.thumbnail,
            duration: video.duration,
            watched_at: new Date().toISOString(),
            watch_count: 1,
          });
      }
      
      // Reload history if on history tab
      if (activeTab === "history") {
        loadHistory();
      }
    } catch (err) {
      console.error('[YouTube] Save history error:', err);
    }
  }, [user, loadHistory, activeTab]);

  // Clear watch history
  const clearHistory = useCallback(async () => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('user_youtube_history')
        .delete()
        .eq('user_id', user.id);
      
      if (error) {
        console.error('[YouTube] Failed to clear history:', error);
        toast.error(t("searchError"));
        return;
      }
      
      setHistoryVideos([]);
      toast.success(t("historyCleared"));
    } catch (err) {
      console.error('[YouTube] Clear history error:', err);
    }
  }, [user, t]);

  // Reset search phase and page when switching tabs
  useEffect(() => {
    if (activeTab !== "search") {
      setSearchPhase("typing");
    }
    setVideoPage(0);
  }, [activeTab]);

  // Load history when user changes or tab changes to history
  useEffect(() => {
    if (activeTab === "history" && user) {
      loadHistory();
    }
  }, [activeTab, user, loadHistory]);

  // Fetch Google access token from session (for personalized feed)
  // Note: YouTube works without Google token (uses API key), but personalized feed needs it
  useEffect(() => {
    const fetchToken = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.provider_token) {
          // Google OAuth session has provider_token
          setGoogleAccessToken(session.provider_token);
        }
        // Don't call refreshSession() - it causes rate limiting issues
        // If no provider_token, YouTube will use the project API key instead
      } catch (err) {
        console.error('[YouTube] Error fetching session:', err);
      }
    };
    
    if (user && isGoogleUser) {
      fetchToken();
    } else {
      setGoogleAccessToken(null);
    }
  }, [user, isGoogleUser]);

  // Handle connecting Google for YouTube
  // For email users, show dialog to ask if they want to link Google
  const handleConnectGoogle = () => {
    // Wait for auth to hydrate before deciding what to do — otherwise a logged-in
    // email user briefly looks logged-out and we'd accidentally replace the session.
    if (authLoading) {
      toast.info(t("searchError"));
      return;
    }
    if (isEmailUser && !hasGoogleLinked) {
      // Email user who hasn't linked Google yet → ask before doing anything.
      setShowLinkAccountDialog(true);
    } else if (isEmailUser && hasGoogleLinked) {
      // Already linked → re-consent through linkIdentity (does NOT swap session).
      handleLinkGoogle();
    } else if (!user) {
      // Truly logged out → fresh Google sign-in is fine.
      handleSignInWithGoogle();
    } else {
      // Already a Google-only user → fresh sign-in to refresh tokens.
      handleSignInWithGoogle();
    }
  };

  // Sign in with Google (replaces session or creates new one)
  const handleSignInWithGoogle = async () => {
    setIsConnectingGoogle(true);
    try {
      const { error } = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: window.location.origin + "/youtube",
        extraParams: {
          access_type: "offline",
          prompt: "consent",
        },
      });
      
      if (error) {
        console.error('[YouTube] Google OAuth error:', error);
        toast.error(t("searchError"));
      }
    } catch (err) {
      console.error('[YouTube] OAuth exception:', err);
      toast.error(t("searchError"));
    } finally {
      setIsConnectingGoogle(false);
    }
  };

  // Link Google to existing email account
  const handleLinkGoogle = async () => {
    setShowLinkAccountDialog(false);
    setIsConnectingGoogle(true);
    try {
      // Use Supabase's linkIdentity to add Google to existing account
      const { error } = await supabase.auth.linkIdentity({
        provider: 'google',
        options: {
          redirectTo: window.location.origin + "/youtube",
          queryParams: {
            access_type: "offline",
            prompt: "consent",
          },
        },
      });
      
      if (error) {
        console.error('[YouTube] Link identity error:', error);
        toast.error(t("searchError"));
      }
    } catch (err) {
      console.error('[YouTube] Link exception:', err);
      toast.error(t("searchError"));
    } finally {
      setIsConnectingGoogle(false);
    }
  };

  // Use Google for YouTube only (sign in with Google, replacing email session)
  const handleGoogleOnlyForYouTube = () => {
    setShowLinkAccountDialog(false);
    handleSignInWithGoogle();
  };

  // Load home feed - works for all users (uses API key on backend)
  // If user has Google token, will show personalized feed
  const loadHomeFeed = useCallback(async () => {
    setIsLoadingHome(true);
    try {
      const headers: Record<string, string> = {};
      // Include Google token if available for personalized feed
      if (googleAccessToken) {
        headers['x-google-access-token'] = googleAccessToken;
      }
      
      const { data, error } = await supabase.functions.invoke('youtube-search', {
        body: { endpoint: 'home', maxResults: 12, lang },
        headers,
      });

      if (error) {
        console.error('[YouTube] Home feed error:', error);
        return;
      }

      if (data?.code === 'AUTH_EXPIRED') {
        setGoogleAccessToken(null);
        // Don't show error - just reload without token
        loadHomeFeed();
        return;
      }

      if (data?.videos) {
        setHomeVideos(data.videos);
      }
    } catch (err) {
      console.error('[YouTube] Home feed exception:', err);
    } finally {
      setIsLoadingHome(false);
    }
  }, [googleAccessToken]);

  // Load home feed when tab changes to home
  useEffect(() => {
    if (activeTab === "home" && homeVideos.length === 0) {
      loadHomeFeed();
    }
  }, [activeTab, homeVideos.length, loadHomeFeed]);

  // Load YouTube IFrame API
  useEffect(() => {
    if (window.YT) {
      setPlayerReady(true);
      return;
    }

    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);

    window.onYouTubeIframeAPIReady = () => {
      setPlayerReady(true);
    };
  }, []);

  // Initialize player when video is selected
  useEffect(() => {
    if (!currentVideo || !playerReady || !currentVideo.videoId) return;

    // Reset initialized state for new video
    setPlayerInitialized(false);

    // Destroy existing player
    if (playerRef.current) {
      try {
        playerRef.current.destroy();
      } catch (e) {
        console.warn('[YouTube] Error destroying player:', e);
      }
      playerRef.current = null;
    }

    // Wait for DOM element to be available (since it's conditionally rendered)
    const initPlayer = () => {
      const playerElement = document.getElementById('youtube-player');
      if (!playerElement) {
        // Retry after a short delay if element isn't ready yet
        setTimeout(initPlayer, 100);
        return;
      }

      // Create new player - store reference in onReady when fully initialized
      new window.YT.Player('youtube-player', {
        videoId: currentVideo.videoId,
        playerVars: {
          autoplay: 1,
          controls: 0,
          modestbranding: 1,
          rel: 0,
          showinfo: 0,
          fs: 0,
        },
        events: {
          onReady: (event: any) => {
            // Store the fully initialized player in the ref
            playerRef.current = event.target;
            setPlayerInitialized(true);
            setDuration(event.target.getDuration());
            setIsPlaying(true);
            startTimeUpdate();
          },
          onStateChange: (event: any) => {
            setIsPlaying(event.data === window.YT.PlayerState.PLAYING);
          },
          onError: (event: any) => {
            console.error('[YouTube] Player error:', event.data);
          },
        },
      });
    };

    // Start initialization attempt
    initPlayer();

    return () => {
      stopTimeUpdate();
    };
  }, [currentVideo, playerReady]);

  const startTimeUpdate = useCallback(() => {
    stopTimeUpdate();
    timeUpdateInterval.current = setInterval(() => {
      if (playerRef.current && playerRef.current.getCurrentTime) {
        setCurrentTime(playerRef.current.getCurrentTime());
      }
    }, 1000);
  }, []);

  const stopTimeUpdate = useCallback(() => {
    if (timeUpdateInterval.current) {
      clearInterval(timeUpdateInterval.current);
      timeUpdateInterval.current = null;
    }
  }, []);

  const handleVideoClick = useCallback((video: VideoItem) => {
    if (video.videoId) {
      setVideoSource(activeTab === "search" ? "search" : "home");
      setCurrentVideo(video);
      // Save to history when user is logged in
      saveToHistory(video);
    }
  }, [saveToHistory, activeTab]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setIsSearching(true);
    try {
      const headers: Record<string, string> = {};
      // Include Google token if available (for personalized results)
      if (googleAccessToken) {
        headers['x-google-access-token'] = googleAccessToken;
      }
      
      const { data, error } = await supabase.functions.invoke('youtube-search', {
        body: { query: searchQuery.trim(), maxResults: 12, lang },
        headers,
      });

      if (error) {
        console.error('[YouTube] Search error:', error);
        toast.error(t("searchError"));
        return;
      }

      if (data?.code === 'AUTH_EXPIRED') {
        setGoogleAccessToken(null);
        // Retry without token
        handleSearch();
        return;
      }

      if (data?.error) {
        console.error('[YouTube] API error:', data.error);
        toast.error(t("searchError"));
        return;
      }

      if (data?.videos) {
        setSearchResults(data.videos);
        setSearchPhase("results");
        if (data.videos.length === 0) {
          toast.info(t("noResults"));
        }
      }
    } catch (err) {
      console.error('[YouTube] Search exception:', err);
      toast.error(t("searchError"));
    } finally {
      setIsSearching(false);
    }
  };

  const handlePlayPause = useCallback(() => {
    if (!playerRef.current || !playerInitialized) return;
    if (typeof playerRef.current.pauseVideo !== 'function') return;
    if (isPlaying) {
      playerRef.current.pauseVideo();
    } else {
      playerRef.current.playVideo();
    }
  }, [isPlaying, playerInitialized]);

  const handleMute = useCallback(() => {
    if (!playerRef.current || !playerInitialized) return;
    if (typeof playerRef.current.mute !== 'function') return;
    if (isMuted) {
      playerRef.current.unMute();
    } else {
      playerRef.current.mute();
    }
    setIsMuted(!isMuted);
  }, [isMuted, playerInitialized]);

  const handleSkipForward = useCallback(() => {
    if (!playerRef.current || !playerInitialized) return;
    if (typeof playerRef.current.seekTo !== 'function') return;
    const newTime = Math.min(currentTime + 10, duration);
    playerRef.current.seekTo(newTime, true);
    setCurrentTime(newTime);
  }, [currentTime, duration, playerInitialized]);

  const handleSkipBackward = useCallback(() => {
    if (!playerRef.current || !playerInitialized) return;
    if (typeof playerRef.current.seekTo !== 'function') return;
    const newTime = Math.max(currentTime - 10, 0);
    playerRef.current.seekTo(newTime, true);
    setCurrentTime(newTime);
  }, [currentTime, playerInitialized]);

  const handleClose = useCallback(() => {
    if (playerRef.current) {
      try {
        playerRef.current.destroy();
      } catch {}
      playerRef.current = null;
    }
    stopTimeUpdate();
    setPlayerInitialized(false);
    setCurrentVideo(null);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    // Always stay on the YouTube page (close the player, don't navigate away)
  }, [stopTimeUpdate]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <MainLayout onBack={currentVideo ? handleClose : undefined} hideSidebar={isMediaRestMode}>
      <div className="flex-1 flex flex-col overflow-hidden" dir={isRTL ? "rtl" : "ltr"}>
        {/* Header with tabs */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border flex-shrink-0">
          {/* YouTube icon + title */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="w-9 h-9 bg-destructive rounded-lg flex items-center justify-center">
              <Play className="w-5 h-5 text-destructive-foreground fill-destructive-foreground" />
            </div>
            <h1 className="text-xl font-bold text-foreground">{t("youtube")}</h1>
          </div>

          {/* Separator */}
          <div className="w-px h-8 bg-border flex-shrink-0" />

          {/* Tab buttons — player-button style, only when not in player */}
          {!currentVideo && (
            <div data-scan-row="yt-tabs" className="flex items-center gap-2 flex-1">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    data-scan-item
                    data-scan-cursor-target="yt-first-video"
                    onClick={() => setActiveTab(tab.id)}
                    className={cn(
                      "relative flex flex-row items-center justify-center gap-2 py-2 px-3 rounded-xl flex-1",
                      "border-2 shadow-md transition-all duration-200",
                      "hover:scale-[1.02] active:scale-[0.98]",
                      isActive
                        ? "bg-primary border-primary"
                        : "bg-card border-border hover:border-primary/40",
                    )}
                  >
                    <Icon
                      className={cn(
                        "flex-shrink-0",
                        isActive ? "text-primary-foreground" : "text-primary"
                      )}
                      style={{ width: 'calc(clamp(1rem, 2vh, 1.5rem) * var(--font-scale, 1))', height: 'calc(clamp(1rem, 2vh, 1.5rem) * var(--font-scale, 1))' }}
                    />
                    <span
                      className={cn(
                        "font-medium leading-none",
                        isActive ? "text-primary-foreground" : "text-card-foreground"
                      )}
                      style={{ fontSize: 'calc(clamp(1rem, 2vh, 1.5rem) * var(--font-scale, 1))' }}
                    >
                      {tab.label}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex-1 flex overflow-hidden">
          {/* Main Content */}
          <div className="flex-1 flex flex-col overflow-hidden p-4">
            <AnimatePresence mode="wait">
              {currentVideo ? (
                <motion.div
                  key="player"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="flex flex-col h-full gap-2"
                >
                  {/* Video Player — maximized */}
                  <div className="relative bg-black rounded-2xl overflow-hidden flex-1 min-h-0 w-full">
                    <div id="youtube-player" className="absolute inset-0 w-full h-full" />
                  </div>

                  {/* Controls or Media Rest Mode */}
                  {isMediaRestMode ? (
                    <>
                      <MediaRestMode isOpen={isMediaRestMode} onWake={() => setIsMediaRestMode(false)} />
                      {/* Video Title + Time — visible during rest mode too */}
                      <div className="flex items-center gap-2 px-1 min-h-0">
                        <p
                          className="font-semibold text-foreground truncate flex-1"
                          style={{ fontSize: 'clamp(1.1rem, 2.4vh, 1.5rem)' }}
                          dir={isRTL ? "rtl" : "ltr"}
                        >
                          {currentVideo.title}
                          {currentVideo.channel && (
                            <span className="text-muted-foreground font-normal ms-2">· {currentVideo.channel}</span>
                          )}
                        </p>
                        <span className="font-bold text-primary font-mono leading-tight flex-shrink-0" style={{ fontSize: 'clamp(1rem, 2.2vh, 1.4rem)' }}>
                          {formatTime(currentTime)} / {formatTime(duration)}
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Single-row controls */}
                      <div className="flex items-stretch gap-2 px-1">
                        {[
                          {
                            idx: 0,
                            onClick: handlePlayPause,
                            icon: isPlaying ? <Pause className="w-5 h-5 text-primary" /> : <Play className="w-5 h-5 text-primary" />,
                            label: isPlaying ? t("pauseVideo") : t("playVideo"),
                            border: "border-primary/30",
                          },
                          {
                            idx: 1,
                            onClick: handleClose,
                            icon: <X className="w-5 h-5 text-primary" />,
                            label: t("closeVideo"),
                            border: "border-border",
                          },
                          {
                            idx: 2,
                            onClick: () => setIsMediaRestMode(true),
                            icon: <Moon className="w-5 h-5 text-primary" />,
                            label: t("rest"),
                            border: "border-border",
                          },
                          {
                            idx: 3,
                            onClick: handleMute,
                            icon: isMuted ? <Volume2 className="w-5 h-5 text-primary" /> : <VolumeX className="w-5 h-5 text-primary" />,
                            label: isMuted ? t("unmuteVideo") : t("muteVideo"),
                            border: "border-border",
                          },
                          {
                            idx: 4,
                            onClick: handleSkipBackward,
                            icon: isRTL ? <SkipForward className="w-5 h-5 text-primary" /> : <SkipBack className="w-5 h-5 text-primary" />,
                            label: t("skipBackward"),
                            border: "border-border",
                          },
                          {
                            idx: 5,
                            onClick: handleSkipForward,
                            icon: isRTL ? <SkipBack className="w-5 h-5 text-primary" /> : <SkipForward className="w-5 h-5 text-primary" />,
                            label: t("skipForward"),
                            border: "border-border",
                          },
                        ].map(({ idx, onClick, icon, label, border }) => (
                          <button
                            key={idx}
                            data-scan-item
                            onClick={onClick}
                            className={cn(
                              "relative flex flex-col items-center justify-center gap-1 py-3 px-2 rounded-xl flex-1",
                              "bg-card border-2 shadow-md transition-all duration-200",
                              border,
                              "hover:border-primary hover:scale-[1.02] active:scale-[0.98]",
                            )}
                          >
                            {icon}
                            <span className="font-medium leading-none text-card-foreground" style={{ fontSize: 'clamp(1em, 2vh, 1.2em)' }}>{label}</span>
                          </button>
                        ))}
                      </div>

                      {/* Video Title + Time — single compact line */}
                      <div className="flex items-center gap-2 px-1 min-h-0">
                        <p
                          className="font-semibold text-foreground truncate flex-1"
                          style={{ fontSize: 'clamp(1.1rem, 2.4vh, 1.5rem)' }}
                          dir={isRTL ? "rtl" : "ltr"}
                        >
                          {currentVideo.title}
                          {currentVideo.channel && (
                            <span className="text-muted-foreground font-normal ms-2">· {currentVideo.channel}</span>
                          )}
                        </p>
                        <span className="font-bold text-primary font-mono leading-tight flex-shrink-0" style={{ fontSize: 'clamp(1rem, 2.2vh, 1.4rem)' }}>
                          {formatTime(currentTime)} / {formatTime(duration)}
                        </span>
                      </div>
                    </>
                  )}
                </motion.div>
              ) : activeTab === "search" && user && searchPhase === "typing" ? (
                /* Search typing mode - full-screen keyboard */
                <motion.div
                  key="search-keyboard"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex-1 flex flex-col overflow-hidden"
                >
                  <YouTubeSearchKeyboard
                    value={searchQuery}
                    onChange={setSearchQuery}
                    onSearch={handleSearch}
                    className="flex-1"
                  />
                  {isSearching && (
                    <div className="absolute inset-0 flex items-center justify-center bg-background/50 z-10">
                      <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    </div>
                  )}
                </motion.div>
              ) : (
                <motion.div
                  key="browse"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex-1 flex flex-col min-h-0"
                >
                  {/* Search results bar - shown after search */}
                  {activeTab === "search" && user && searchPhase === "results" && (
                    <div className="flex items-center gap-2 mb-2" dir={isRTL ? "rtl" : "ltr"}>
                      <div className="flex-1 px-4 py-3 rounded-xl bg-card border-2 border-border h-full flex items-center" style={{ minHeight: "clamp(60px, 8vh, 130px)" }}>
                        <span className="text-lg font-medium text-foreground">{searchQuery}</span>
                      </div>
                      <button
                        data-scan-item
                        onClick={() => {
                          setSearchPhase("typing");
                          setSearchQuery("");
                          setSearchResults([]);
                        }}
                        className={cn(
                          "flex flex-col items-center justify-center gap-1 py-2 px-1 rounded-xl cursor-pointer shrink-0",
                          "bg-card border-2 border-border shadow-md transition-all duration-200",
                          "hover:border-primary hover:scale-[1.02] active:scale-[0.98]"
                        )}
                        style={{ width: "clamp(80px, 10vw, 140px)", minHeight: "clamp(60px, 8vh, 130px)" }}
                      >
                        <Search style={{ width: "clamp(1.1rem, 2.8vh, 2.4rem)", height: "clamp(1.1rem, 2.8vh, 2.4rem)" }} className="flex-shrink-0 text-primary" />
                        <span style={{ fontSize: "clamp(0.65rem, 1.7vh, 1.1rem)" }} className="font-medium text-card-foreground">{t("newSearch")}</span>
                      </button>
                    </div>
                  )}

                  {/* Home tab loading */}
                  {activeTab === "home" && isLoadingHome && (
                    <div className="flex justify-center py-16">
                      <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    </div>
                  )}

                  {/* History tab - show login prompt if not logged in */}
                  {activeTab === "history" && !user && (
                    <div className="flex flex-col items-center justify-center py-16 gap-6">
                      <Clock className="w-16 h-16 text-muted-foreground" />
                      <p className="text-xl text-muted-foreground">{t("loginToWatch")}</p>
                      <Button
                        data-scan-item
                        onClick={() => navigate("/auth")}
                        className="flex items-center gap-2"
                        size="lg"
                      >
                        <LogIn className="w-5 h-5" />
                        {t("login")}
                      </Button>
                    </div>
                  )}

                  {/* History tab - clear button (no title — tab already identifies the section) */}
                  {activeTab === "history" && user && (
                    <>

                      {isLoadingHistory && (
                        <div className="flex justify-center py-8">
                          <Loader2 className="w-8 h-8 animate-spin text-primary" />
                        </div>
                      )}

                      {!isLoadingHistory && historyVideos.length === 0 && (
                        <div className="flex flex-col items-center justify-center py-16 gap-4">
                          <Clock className="w-16 h-16 text-muted-foreground" />
                          <p className="text-xl text-muted-foreground">{t("noHistory")}</p>
                        </div>
                      )}
                    </>
                  )}


                  {/* Paginated video grid */}
                  {user && (activeTab !== "history" || historyVideos.length > 0) && displayVideos.length > 0 && (
                    <div className="flex-1 flex items-stretch gap-3 min-h-0">
                      {/* Prev arrow — direction-aware */}
                      <div className="relative flex-shrink-0 w-12" data-scan-row="pagination-prev">
                        <button
                          {...(hasPrevVideoPage ? { 'data-scan-item': '' } : {})}
                          data-scan-cursor-target="yt-first-video"
                          onClick={() => hasPrevVideoPage && setVideoPage(p => p - 1)}
                          className={cn(
                            "w-full h-full flex items-center justify-center rounded-xl border-2 shadow-sm transition-all",
                            hasPrevVideoPage
                              ? "bg-card border-primary/40 hover:bg-primary/10 cursor-pointer"
                              : "border-transparent bg-transparent shadow-none cursor-default",
                          )}
                        >
                          {hasPrevVideoPage && (isRTL ? <ChevronRight className="w-6 h-6 text-primary" strokeWidth={3} /> : <ChevronLeft className="w-6 h-6 text-primary" strokeWidth={3} />)}
                        </button>
                      </div>

                      {/* Video grid: 2 rows × 3 cols — each row is a separate scan row */}
                      <div
                        className="flex-1 min-h-0 flex flex-col gap-3"
                      >
                        {[pageVideos.slice(0, 3), pageVideos.slice(3, 6)].map((rowVideos, rowIdx) => (
                          <div
                            key={`video-row-${rowIdx}`}
                            data-scan-row={`yt-videos-row-${rowIdx}`}
                            className="flex-1 min-h-0 grid grid-cols-3 gap-3"
                          >
                          {rowVideos.map((video, colIdx) => {
                            const videoIdx = rowIdx * 3 + colIdx;
                            return (
                          <div
                            key={video.id}
                            data-scan-item
                            {...(videoIdx === 0 ? { 'data-scan-default': '', 'data-scan-cursor-id': 'yt-first-video', 'data-scan-cursor-level': 'row' } : {})}
                            className="group cursor-pointer rounded-xl transition-all relative flex flex-col h-full overflow-hidden"
                            onClick={() => handleVideoClick(video)}
                          >
                            <div className="relative rounded-xl overflow-hidden bg-muted" style={{ aspectRatio: '16/9' }}>
                              <img
                                src={video.thumbnail}
                                alt={video.title}
                                className="absolute inset-0 w-full h-full object-cover transition-transform group-hover:scale-105"
                              />
                              <div className="absolute bottom-2 left-2 bg-black/80 text-white text-xs px-2 py-1 rounded">
                                {video.duration}
                              </div>
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                                <Play className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity fill-white" />
                              </div>
                            </div>
                            <div className="flex-1 flex flex-col py-2 px-2">
                              <h3 className="font-medium text-foreground line-clamp-2 text-sm leading-snug">{video.title}</h3>
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{video.channel}</p>
                              {video.views && (
                                <p className="text-xs text-muted-foreground mt-0.5">{video.views}</p>
                              )}
                            </div>
                          </div>
                            );
                          })}
                          </div>
                        ))}
                      </div>

                      {/* Next arrow — direction-aware + page indicator */}
                      <div className="relative flex-shrink-0 w-12 flex flex-col items-center" data-scan-row="pagination-next">
                        {totalVideoPages > 1 && (
                          <span className="text-xs font-medium text-muted-foreground mb-1 whitespace-nowrap">
                            {videoPage + 1} / {totalVideoPages}
                          </span>
                        )}
                        <button
                          {...(hasNextVideoPage ? { 'data-scan-item': '' } : {})}
                          data-scan-cursor-target="yt-first-video"
                          onClick={() => hasNextVideoPage && setVideoPage(p => p + 1)}
                          className={cn(
                            "w-full flex-1 flex items-center justify-center rounded-xl border-2 shadow-sm transition-all",
                            hasNextVideoPage
                              ? "bg-card border-primary/40 hover:bg-primary/10 cursor-pointer"
                              : "border-transparent bg-transparent shadow-none cursor-default",
                          )}
                        >
                          {hasNextVideoPage && (isRTL ? <ChevronLeft className="w-6 h-6 text-primary" strokeWidth={3} /> : <ChevronRight className="w-6 h-6 text-primary" strokeWidth={3} />)}
                        </button>
                      </div>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Link Google Account Dialog */}
      <Dialog open={showLinkAccountDialog} onOpenChange={setShowLinkAccountDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5" />
              {t("linkGoogleAccount") || "Link Google Account"}
            </DialogTitle>
            <DialogDescription>
              {t("linkGoogleDescription") || "Do you want to add Google sign-in to your account? This will let you sign in with either email or Google."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              data-scan-item
              variant="outline"
              onClick={handleGoogleOnlyForYouTube}
              className="w-full sm:w-auto"
            >
              {t("useGoogleOnlyForYouTube") || "Just for YouTube"}
            </Button>
            <Button
              data-scan-item
              onClick={handleLinkGoogle}
              className="w-full sm:w-auto"
            >
              <Link2 className="w-4 h-4 mr-2" />
              {t("linkToMyAccount") || "Link to my account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}
